/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  CircularProgress,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const formatCurrency = (value, lang) => {
  if (value == null) return '-';
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Payroll() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'ADMIN';
  const [tab, setTab] = useState(0);
  const [month, setMonth] = useState(getCurrentMonth());
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [ruleDrafts, setRuleDrafts] = useState([]);
  const isAllUsers = isAdmin && selectedUserId === 'all';

  const fetchSummary = async (userId) => {
    try {
      const res = await API.get('/payroll/summary', {
        params: { month, user_id: userId || undefined },
      });
      setSummary(res.data);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement', 'فشل التحميل'), 'error');
    }
  };

  const fetchSales = async (userId) => {
    try {
      const params = { month };
      if (selectedDate) params.date = selectedDate;
      let endpoint = '/logs/me';
      if (isAdmin && userId === 'all') {
        endpoint = '/logs';
      } else if (isAdmin && userId) {
        endpoint = `/logs/seller/${userId}`;
      }
      const res = await API.get(endpoint, { params });
      setSales(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des ventes', 'فشل تحميل المبيعات'), 'error');
    }
  };

  const fetchUsersAndRules = async () => {
    if (!isAdmin) return;
    try {
      const [usersRes, rulesRes] = await Promise.all([
        API.get('/users'),
        API.get('/payroll/rules'),
      ]);
      const usersData = usersRes.data || [];
      const rulesData = rulesRes.data || [];
      setUsers(usersData);
      if (usersData.length > 0) {
        setSelectedUserId((prev) => prev || 'all');
      }

      const merged = usersData.map((u) => {
        const rule = rulesData.find((r) => r.user_id === u.id);
        return {
          user_id: u.id,
          username: u.username,
          role: u.role,
          base_salary: rule?.base_salary ?? 0,
          commission_type: rule?.commission_type ?? 'REVENUE',
          commission_rate: rule?.commission_rate ?? 0,
          target_value: rule?.target_value ?? 0,
          active: rule?.active !== 0,
        };
      });
      setRuleDrafts(merged);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement', 'فشل التحميل'), 'error');
    }
  };

  const fetchHistory = async (userId) => {
    try {
      const res = await API.get('/payroll/history', {
        params: { user_id: userId || undefined, limit: 12 },
      });
      setHistory(res.data?.series || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr("Erreur lors du chargement de l'historique", 'فشل تحميل السجل'), 'error');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    const userId = isAdmin ? selectedUserId : undefined;
    Promise.all([fetchSummary(userId), fetchSales(userId), fetchHistory(userId)]).finally(() => setLoading(false));
  }, [month, selectedUserId, selectedDate, isAdmin]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUsersAndRules();
  }, [isAdmin]);

  const handleRuleChange = (index, field, value) => {
    setRuleDrafts((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const saveRule = async (rule) => {
    try {
      await API.post('/payroll/rules', {
        user_id: rule.user_id,
        base_salary: Number(rule.base_salary),
        commission_type: rule.commission_type,
        commission_rate: Number(rule.commission_rate),
        target_value: Number(rule.target_value),
        active: !!rule.active,
      });
      showSnackbar(tr('Regle enregistree', 'تم حفظ القاعدة'), 'success');
      fetchUsersAndRules();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la sauvegarde', 'فشل الحفظ'), 'error');
    }
  };

  const achievedPercent = useMemo(() => {
    const target = summary?.targetSalary || 0;
    const achieved = summary?.achievedSalary || 0;
    if (!target) return 0;
    return Math.min(100, Math.round((achieved / target) * 100));
  }, [summary]);


  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Mes ventes & salaire', 'مبيعاتي وراتبي')}
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 520 }}>
          {tr('Suivi des ventes et de la commission.', 'متابعة المبيعات والعمولة.')}
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background: 'var(--surface-1)',
          border: '1px solid var(--border-subtle)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="month"
            label={tr('Mois', 'الشهر')}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 180 }}
          />
          <TextField
            type="date"
            label={tr('Jour', 'اليوم')}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 160 }}
          />
          {isAdmin && (
            <TextField
              select
              label={tr('Employe', 'الموظف')}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 200 }}
              size="small"
            >
              <MenuItem value="all">{tr('Tous', 'الكل')}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.username}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isAdmin && (
            <Tabs
              value={tab}
              onChange={(e, newValue) => setTab(newValue)}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{
                flex: 1,
                '& .MuiTab-root': { color: 'var(--text-muted)', fontWeight: 700 },
                '& .Mui-selected': { color: 'var(--text-primary)' },
              }}
            >
              <Tab label={tr('Vue employe', 'عرض الموظف')} />
              <Tab label={tr('Historique paiement', 'سجل الدفع')} />
              <Tab label={tr('Parametres', 'الإعدادات')} />
            </Tabs>
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : (
        <>
          {(!isAdmin || tab === 0) && (
            <>
              {isMobile ? (
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    mb: 3,
                    background: 'var(--surface-1)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      {tr('Resume du mois', 'ملخص الشهر')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack direction="column" spacing={2}>
                      {[
                        { label: isAllUsers ? tr('Total fixes', 'إجمالي الثابت') : tr('Salaire fixe', 'راتب ثابت'), value: formatCurrency(summary?.base_salary, lang) },
                        { label: isAllUsers ? tr('Total commissions', 'إجمالي العمولات') : tr('Commission', 'العمولة'), value: formatCurrency(summary?.commission, lang) },
                        { label: isAllUsers ? tr('Total salaires', 'إجمالي الرواتب') : tr('Salaire atteint', 'الراتب المحقق'), value: formatCurrency(summary?.achievedSalary, lang) },
                        { label: isAllUsers ? tr('Total objectifs', 'إجمالي الأهداف') : tr('Objectif', 'الهدف'), value: formatCurrency(summary?.targetSalary, lang) },
                      ].map((item) => (
                        <Paper
                          key={item.label}
                          sx={{
                            p: 2.5,
                            borderRadius: 4,
                            background: 'var(--surface-2)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 20 }}>
                            {item.value}
                          </Typography>
                        </Paper>
                      ))}
                      <Paper
                        sx={{
                          p: 2.5,
                          borderRadius: 4,
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                          {tr('Progression objectif', 'تقدم الهدف')}
                        </Typography>
                        <Box
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            background: 'var(--surface-3)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${achievedPercent}%`,
                              background: 'linear-gradient(90deg, #16a34a, #059669)',
                            }}
                          />
                        </Box>
                        <Typography sx={{ color: 'var(--text-muted)', mt: 1 }}>
                          {achievedPercent}% {tr('atteint', 'محقق')}
                        </Typography>
                      </Paper>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
                  {[
                    { label: isAllUsers ? tr('Total fixes', 'إجمالي الثابت') : tr('Salaire fixe', 'راتب ثابت'), value: formatCurrency(summary?.base_salary, lang) },
                    { label: isAllUsers ? tr('Total commissions', 'إجمالي العمولات') : tr('Commission', 'العمولة'), value: formatCurrency(summary?.commission, lang) },
                    { label: isAllUsers ? tr('Total salaires', 'إجمالي الرواتب') : tr('Salaire atteint', 'الراتب المحقق'), value: formatCurrency(summary?.achievedSalary, lang) },
                    { label: isAllUsers ? tr('Total objectifs', 'إجمالي الأهداف') : tr('Objectif', 'الهدف'), value: formatCurrency(summary?.targetSalary, lang) },
                  ].map((item) => (
                    <Paper
                      key={item.label}
                      sx={{
                        flex: 1,
                        p: 3,
                        borderRadius: 4,
                        background:
                          'var(--surface-1)',
                        border: '1px solid var(--border-subtle)',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 22 }}>
                        {item.value}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}


              {!isMobile && (
                <Paper
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 4,
                    background:
                      'var(--surface-1)',
                    border: '1px solid var(--border-subtle)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 1 }}>
                    {tr('Progression objectif', 'تقدم الهدف')}
                  </Typography>
                  <Box
                    sx={{
                      height: 12,
                      borderRadius: 999,
                      background: 'var(--surface-3)',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${achievedPercent}%`,
                        background: 'linear-gradient(90deg, #16a34a, #059669)',
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: 'var(--text-muted)', mt: 1 }}>
                    {achievedPercent}% {tr('atteint', 'محقق')}
                  </Typography>
                </Paper>
              )}

              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                  {tr('Ventes du mois', 'مبيعات الشهر')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          ...(isAllUsers ? ['Employe'] : []),
                          tr('Reference', 'المرجع'),
                          tr('Quantite', 'الكمية'),
                          tr('Total', 'الإجمالي'),
                          tr('Date', 'التاريخ'),
                        ].map((label) => (
                          <TableCell
                            key={label}
                            sx={{
                              color: 'var(--text-primary)',
                              fontWeight: 700,
                              backgroundColor: 'var(--table-header-bg)',
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sales.map((row) => (
                        <TableRow key={row.id}>
                          {isAllUsers && (
                            <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                              {row.username || '-'}
                            </TableCell>
                          )}
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.reference}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.quantity}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {formatCurrency(row.total_price, lang)}
                          </TableCell>
                          <TableCell sx={{ color: 'var(--text-secondary)' }}>
                            {row.created_at}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sales.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isAllUsers ? 5 : 4} sx={{ color: 'var(--text-muted)' }}>
                            {tr('Aucune vente pour ce mois.', 'لا توجد مبيعات لهذا الشهر.')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

          {isAdmin && tab === 1 && (
            <Paper
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                {tr('Historique (12 derniers mois)', 'السجل (آخر 12 شهر)')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        ...(isAllUsers ? ['Employe'] : []),
                        tr('Mois', 'الشهر'),
                        tr('Revenu', 'الإيراد'),
                        tr('Quantite', 'الكمية'),
                        tr('Commission', 'العمولة'),
                        tr('Salaire atteint', 'الراتب المحقق'),
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((row) => (
                      <TableRow key={`${row.month}-${row.user_id || 'me'}`}>
                        {isAllUsers && (
                          <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {row.username || '-'}
                          </TableCell>
                        )}
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.month}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.revenue, lang)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.quantity}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.commission, lang)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.achievedSalary, lang)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAllUsers ? 6 : 5} sx={{ color: 'var(--text-muted)' }}>
                          {tr('Aucune donnee disponible.', 'لا توجد بيانات متاحة.')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {isAdmin && tab === 2 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                background:
                  'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  {tr('Parametres de commission', 'إعدادات العمولة')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      const res = await API.get('/payroll/export.csv', {
                        responseType: 'blob',
                        params: { month },
                      });
                      const blob = new Blob([res.data], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `payroll-${month}.csv`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      showSnackbar(err.response?.data?.message || tr("Erreur lors de l'export", 'فشل التصدير'), 'error');
                    }
                  }}
                  sx={{
                    borderRadius: 999,
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {tr('Export CSV', 'تصدير CSV')}
                </Button>
              </Stack>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[tr('Employe', 'الموظف'), tr('Fixe', 'ثابت'), tr('Type', 'النوع'), tr('Taux', 'النسبة'), tr('Objectif', 'الهدف'), tr('Actif', 'نشط'), tr('Action', 'إجراء')].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ruleDrafts.map((row, index) => (
                      <TableRow key={row.user_id}>
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.username}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.base_salary}
                            onChange={(e) => handleRuleChange(index, 'base_salary', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.commission_type}
                            onChange={(e) => handleRuleChange(index, 'commission_type', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 120 }}
                          >
                            <option value="REVENUE">{tr('Revenu %', 'نسبة الإيراد')}</option>
                            <option value="UNIT">{tr('Unite', 'وحدة')}</option>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.commission_rate}
                            onChange={(e) => handleRuleChange(index, 'commission_rate', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={row.target_value}
                            onChange={(e) => handleRuleChange(index, 'target_value', e.target.value)}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, maxWidth: 120 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={row.active ? 'yes' : 'no'}
                            onChange={(e) => handleRuleChange(index, 'active', e.target.value === 'yes')}
                            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 90 }}
                          >
                            <option value="yes">{tr('Oui', 'نعم')}</option>
                            <option value="no">{tr('Non', 'لا')}</option>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveRule(row)}
                          >
                            {tr('Enregistrer', 'حفظ')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {ruleDrafts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ color: 'var(--text-muted)' }}>
                          {tr('Aucun employe trouve.', 'لا يوجد موظفون.')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}








